import React, { useState } from 'react';
import StatusBadge from './StatusBadge';
import { STATUTS, PAIEMENTS } from '../data/constants';
import { formatDateTime, formatFCFA } from '../utils/helpers';
import { useApp } from '../data/ColisContext';
import { makeQrSvgDataUri } from '../utils/qrcode';
import ModalPrompt from './ModalPrompt';

export default function ColisCard({ colis, onAffecter, onEdit, readOnly = false }) {
  const [expanded, setExpanded] = useState(false);
  const [prompt, setPrompt] = useState({ open: false, kind: '', title: '', description: '', placeholder: '', multiline: false });
  const { user, mettreEnRoute, marquerLivre, marquerEchec, addProblem, supprimerColis, showToast, addColisToValise, removeColisFromValise /* SUIVI EN DIRECT (pause) , affecterLivreur */ } = useApp();
  const isAgent = user?.agent === true || user?.agent === 1 || user?.agent === '1';

  const amountDue = colis?.meta?.amount_due;
  const hasAmountDue = amountDue !== undefined && amountDue !== null && amountDue !== '' && Number(amountDue) > 0;
  const paymentRequired = colis?.meta?.payment_required === true || colis?.meta?.payment_required === 1 || colis?.meta?.payment_required === '1';
  const paymentSettled = !paymentRequired;

  const valiseId = colis?.meta?.valise_id ? String(colis.meta.valise_id) : '';
  const colisIdUpper = String(colis?.id || '').toUpperCase();
  const isValise = colisIdUpper.startsWith('VAL-') || String(colis?.meta?.type || '').toLowerCase() === 'valise';
  const valiseContents = isValise && Array.isArray(colis?.meta?.contents) ? colis.meta.contents : [];

  const motifEchec = colis?.motif_echec || colis?.motif || '';
  const trackUrl = `${window.location.origin}/details?code=${encodeURIComponent(colis?.id || '')}`;
  /* SUIVI EN DIRECT (pause)
  const liveToken = colis?.meta?.live_token || '';
  const liveShareUrl = liveToken
    ? `${window.location.origin}/livreur?code=${encodeURIComponent(colis?.id || '')}&token=${encodeURIComponent(liveToken)}`
    : '';
  */
  const qrPayload = String(colis?.id || '').replace(/-/g, '');
  let qrImg = null;
  try {
    qrImg = makeQrSvgDataUri(qrPayload, 260, { ecLevel: 'L', quiet: 12 });
  } catch {
    qrImg = null;
  }

  const dot = STATUTS[colis.statut]?.dot || '#4F46E5';
  const paiement = PAIEMENTS[colis.paiement] || { label: colis.paiement, icon: '💳' };

  const openPrompt = (kind) => {
    if (kind === 'motif_echec') {
      setPrompt({
        open: true,
        kind,
        title: '❌ Échec de livraison',
        description: "Saisissez le motif de l'échec.",
        placeholder: "Ex: Client absent, numéro injoignable...",
        multiline: true,
      });
      return;
    }
    if (kind === 'problem') {
      setPrompt({
        open: true,
        kind,
        title: '⚠️ Signaler un problème',
        description: 'Décrivez le problème rencontré.',
        placeholder: 'Ex: Adresse incomplète, numéro injoignable...',
        multiline: true,
      });
      return;
    }
    if (kind === 'valise_add_child') {
      setPrompt({
        open: true,
        kind,
        title: '➕ Ajouter un colis',
        description: 'Code du colis à ajouter dans la valise.',
        placeholder: 'Ex: INT-20260410-XXXXXX',
        multiline: false,
      });
      return;
    }
    if (kind === 'valise_remove_child') {
      setPrompt({
        open: true,
        kind,
        title: '➖ Retirer un colis',
        description: 'Code du colis à retirer de la valise.',
        placeholder: 'Ex: INT-20260410-XXXXXX',
        multiline: false,
      });
      return;
    }
    if (kind === 'add_to_valise') {
      setPrompt({
        open: true,
        kind,
        title: '🧳 Ajouter à une valise',
        description: 'Code de la valise (VAL-...).',
        placeholder: 'Ex: VAL-20260410-ABC123',
        multiline: false,
      });
    }
  };

  return (
    <div style={{
      background:   'white',
      borderRadius: 16,
      overflow:     'hidden',
      boxShadow:    '0 2px 12px rgba(0,0,0,0.06)',
      border:       '1px solid #f0f0f0',
      transition:   'all 0.2s',
      animation:    'fadeIn 0.3s ease-out',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.11)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'}
    >
      {/* Bande couleur top */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${dot}, ${STATUTS[colis.statut]?.color || dot})` }} />

      <div style={{ padding: '16px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#4F46E5', fontFamily: 'monospace', letterSpacing: 1 }}>
              {colis.id}
            </div>
            {colis.nom && (
              <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 800, marginTop: 4 }}>
                {colis.nom}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{formatDateTime(colis.date)}</div>
          </div>
          <StatusBadge statut={colis.statut} />
        </div>

        {/* Expéditeur → Destinataire */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Expéditeur</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{colis.expediteur}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{colis?.meta?.expediteur_phone || '—'}</div>
          </div>
          <span style={{ fontSize: 18, color: '#94a3b8' }}>→</span>
          <div style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Destinataire</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{colis.destinataire}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{colis.tel}</div>
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {colis.poids !== undefined && colis.poids !== null && colis.poids !== '' && <Tag>⚖️ {colis.poids} kg</Tag>}
          {colis.description && <Tag>📦 {colis.description}</Tag>}
          {colis.montant !== undefined && colis.montant !== null && <Tag>💰 {formatFCFA(colis.montant)}</Tag>}
          {hasAmountDue && <Tag>🧾 Montant dû: {formatFCFA(Number(amountDue))}</Tag>}
          {!hasAmountDue && paymentSettled && <Tag>✅ Montant réglé</Tag>}
          {colis.paiement && <Tag>{paiement.icon} {paiement.label}</Tag>}
          {colis.livreur && <Tag accent>🛵 {colis.livreur}</Tag>}
          {!!valiseId && <Tag accent>🧳 Valise: {valiseId}</Tag>}
        </div>

        {colis.statut === 'echec' && motifEchec && (
          <div style={{
            marginBottom: 12,
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 12,
            padding: '10px 12px',
            color: '#991B1B',
            fontSize: 13,
            fontWeight: 700,
          }}>
            Motif : {motifEchec}
          </div>
        )}

        {/* Toggle détails */}
        <button
          onClick={() => setExpanded(x => !x)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#94a3b8', padding: 0, marginBottom: expanded ? 12 : 0 }}
        >
          {expanded ? '▲ Moins de détails' : '▼ Plus de détails'}
        </button>

        {/* Détails */}
        {expanded && (
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Detail label="📍 Adresse">{colis.adresse}</Detail>
            <Detail label="🔲 QR Code"><code style={{ fontSize: 12, background: '#f8fafc', padding: '2px 8px', borderRadius: 6 }}>{colis.qr}</code></Detail>
            {hasAmountDue && <Detail label="🧾 Montant dû">{formatFCFA(Number(amountDue))}</Detail>}
            {!hasAmountDue && paymentSettled && <Detail label="💳 Paiement">Montant réglé</Detail>}
            {!!valiseId && <Detail label="🧳 Valise">{valiseId}</Detail>}
            {isValise && (
              <Detail label="📦 Contenu">
                {valiseContents.length > 0 ? (
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                    {valiseContents.map((cid) => (
                      <code key={cid} style={{ fontSize: 12, background: '#f8fafc', padding: '2px 8px', borderRadius: 6 }}>{String(cid)}</code>
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </Detail>
            )}

            <div style={{
              marginTop: 10,
              background: '#f8fafc',
              borderRadius: 14,
              padding: 12,
              display: 'flex',
              gap: 14,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              {qrImg ? (
                <img
                  src={qrImg}
                  alt="QR"
                  width={130}
                  height={130}
                  style={{ borderRadius: 10, background: 'white' }}
                />
              ) : (
                <div style={{
                  width: 130,
                  height: 130,
                  borderRadius: 10,
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#94a3b8',
                  fontSize: 12,
                  fontWeight: 800,
                }}>
                  QR
                </div>
              )}

              {!!colis.photo && (
                <a href={colis.photo} target="_blank" rel="noreferrer" style={{ display: 'inline-block' }}>
                  <img
                    src={colis.photo}
                    alt="Photo produit"
                    width={90}
                    height={90}
                    style={{ borderRadius: 10, background: 'white', objectFit: 'cover', border: '1px solid #e2e8f0' }}
                  />
                </a>
              )}

              <div style={{ minWidth: 180 }}>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 800, marginBottom: 4 }}>Scanner pour récupérer le code du colis</div>
                <a href={trackUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#4F46E5', fontWeight: 900, textDecoration: 'none', wordBreak: 'break-all' }}>
                  {trackUrl}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
            {!!user?.is_admin && colis.statut === 'depot' && (
              <ActionBtn color="white" bg="#4F46E5" onClick={() => onAffecter(colis.id)}>🛵 Affecter livreur</ActionBtn>
            )}
            {/* SUIVI EN DIRECT (pause)
            {!!user?.is_admin && colis.statut === 'depot' && (
              <ActionBtn
                color="#0f172a"
                bg="#E2E8F0"
                onClick={async () => {
                  const name = window.prompt('Nom du livreur ?');
                  if (!name || !String(name).trim()) return;
                  try {
                    const updated = await affecterLivreur(colis.id, String(name).trim());
                    const token = updated?.meta?.live_token || '';
                    if (!token) {
                      showToast('Affectation OK mais live_token introuvable.', 'error');
                      return;
                    }
                    const url = `${window.location.origin}/livreur?code=${encodeURIComponent(updated?.id || colis.id)}&token=${encodeURIComponent(token)}`;
                    try {
                      await navigator.clipboard.writeText(url);
                    } catch {
                      // ignore
                    }
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (e) {
                    showToast(e?.message || 'Erreur', 'error');
                  }
                }}
              >
                ⚡ Affecter + lien
              </ActionBtn>
            )}

            {!!user?.is_admin && !!liveShareUrl && (
              <ActionBtn
                color="#3730A3"
                bg="#EEF2FF"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(liveShareUrl);
                  } catch {
                    // ignore
                  }
                  window.open(liveShareUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                📍 Lien livreur
              </ActionBtn>
            )}
            */}
            {colis.statut === 'affecte'  && <ActionBtn color="#0284C7" bg="#E0F2FE" onClick={() => mettreEnRoute(colis.id)}>🚀 En route</ActionBtn>}
            {colis.statut === 'en_route' && (
              <>
                <ActionBtn color="white" bg="#059669" onClick={() => marquerLivre(colis.id)}>🎉 Livré</ActionBtn>
                <ActionBtn color="#DC2626" bg="#FEE2E2" onClick={() => openPrompt('motif_echec')}>❌ Échec</ActionBtn>
              </>
            )}
            {!!(user?.is_admin || isAgent) && (
              <ActionBtn
                color="#92400E"
                bg="#FFEDD5"
                onClick={async () => {
                  openPrompt('problem');
                }}
              >
                ⚠️ Problème
              </ActionBtn>
            )}
            {!!user?.is_admin && colis.statut === 'echec' && (
              <ActionBtn
                color="white"
                bg="#059669"
                onClick={() => marquerLivre(colis.id, { clearMotifEchec: true })}
              >
                ✅ Corriger → Livré
              </ActionBtn>
            )}
            {!!user?.is_admin && (
              <ActionBtn color="#0f172a" bg="#E2E8F0" onClick={() => typeof onEdit === 'function' && onEdit(colis.id)}>
                ✏️ Modifier
              </ActionBtn>
            )}

            {!!user?.is_admin && isValise && (
              <>
                <ActionBtn
                  color="#0369A1"
                  bg="#E0F2FE"
                  onClick={async () => {
                    openPrompt('valise_add_child');
                  }}
                >
                  ➕ Ajouter colis
                </ActionBtn>
                <ActionBtn
                  color="#B45309"
                  bg="#FFEDD5"
                  onClick={async () => {
                    openPrompt('valise_remove_child');
                  }}
                >
                  ➖ Retirer colis
                </ActionBtn>
              </>
            )}

            {!!user?.is_admin && !isValise && (
              <ActionBtn
                color="#3730A3"
                bg="#EEF2FF"
                onClick={async () => {
                  openPrompt('add_to_valise');
                }}
              >
                🧳 Ajouter à une valise
              </ActionBtn>
            )}

            {!!user?.is_admin && (
              <ActionBtn color="#DC2626" bg="#FEE2E2" onClick={() => supprimerColis(colis.id)} style={{ marginLeft: 'auto' }}>🗑️</ActionBtn>
            )}
          </div>
        )}
      </div>

      <ModalPrompt
        open={prompt.open}
        title={prompt.title}
        description={prompt.description}
        placeholder={prompt.placeholder}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        multiline={prompt.multiline}
        onClose={() => setPrompt({ open: false, kind: '', title: '', description: '', placeholder: '', multiline: false })}
        onConfirm={async (value) => {
          const v = String(value || '').trim();
          if (!v) {
            showToast('Champ requis', 'error');
            return;
          }
          try {
            if (prompt.kind === 'motif_echec') {
              await marquerEchec(colis.id, v);
            } else if (prompt.kind === 'problem') {
              await addProblem(colis.id, v);
            } else if (prompt.kind === 'valise_add_child') {
              await addColisToValise(colis.id, v);
            } else if (prompt.kind === 'valise_remove_child') {
              await removeColisFromValise(colis.id, v);
            } else if (prompt.kind === 'add_to_valise') {
              await addColisToValise(v, colis.id);
            }
            setPrompt({ open: false, kind: '', title: '', description: '', placeholder: '', multiline: false });
          } catch (e) {
            showToast(e?.message || 'Erreur', 'error');
          }
        }}
      />
    </div>
  );
}

// ─── Sous-composants internes ───────────────────────────────────────────────

function Tag({ children, accent }) {
  return (
    <span style={{
      background:   accent ? '#EEF2FF' : '#f1f5f9',
      borderRadius: 8,
      padding:      '4px 10px',
      fontSize:     12,
      color:        accent ? '#4F46E5' : '#475569',
      fontWeight:   accent ? 700 : 500,
    }}>
      {children}
    </span>
  );
}

function Detail({ label, children }) {
  return (
    <div style={{ fontSize: 12, color: '#64748b' }}>
      <span style={{ fontWeight: 700, color: '#374151' }}>{label} : </span>
      {children}
    </div>
  );
}

function ActionBtn({ children, color, bg, onClick, style }) {
  return (
    <button onClick={onClick} style={{
      flex:         '1',
      minWidth:     90,
      padding:      '9px 12px',
      background:   bg,
      color,
      border:       'none',
      borderRadius: 10,
      fontWeight:   700,
      fontSize:     13,
      cursor:       'pointer',
      fontFamily:   'inherit',
      transition:   'opacity 0.15s',
      ...style,
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      {children}
    </button>
  );
}
